import { useGetAllDirectorsInCompany } from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Icons,
  InputErrorMessage,
  Label,
  Loading,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  BoardPositionEnum,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import { useForm } from '../../../../hooks/useZodForm';
import { omitLegacyOwnershipFlags } from '../../../common/helpers';
import {
  findDirectorByRoleForCorporation,
  getDirectorName,
  groupTitlesByDirectorIds,
} from '../../../components/Director/common';
import { IndividualDirectorFormModal } from '../components/modals/IndividualDirectorForm';
import { validateIndividualDirector } from '../components/modals/IndividualDirectorForm/validators';
import { TUAEIncorporationRequestData } from '../types';
import { keyPositionsSchema, TKeyPositionFieldTypes } from './schema';
import {
  TIndividualKeyPositionFormModalConfig,
  TUAEKeyPositions,
} from './types';

type TKeyPositionProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  next: () => void;
  back: () => void;
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
};

export const KeyPositions = ({
  workRequest,
  setNotification,
  mutate,
  next,
  back,
}: TKeyPositionProps) => {
  const {
    data: allPeopleInCompany,
    error,
    mutate: mutateAllPeople,
  } = useGetAllDirectorsInCompany(workRequest?.companyId);

  const directorOptions = allPeopleInCompany?.directors?.map((director) => ({
    value: director.id.toString(),
    label: getDirectorName(director),
  }));

  const addEditKeyPositionsModalVisibility = useToggle();

  const [individualKeyPositionFormState, setIndividualKeyPositionFormState] =
    useState<TIndividualKeyPositionFormModalConfig>(null);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TKeyPositionFieldTypes>({
    resolver: zodResolver(keyPositionsSchema),
  });

  const onSuccessAddEditKeyPosition = async (newDirector: DirectorBase) => {
    await mutateAllPeople();
    if (directorIdsWithError.has(newDirector.id)) {
      setDirectorIdsWithError((prevIds) => {
        const newIds = new Set(prevIds);
        newIds.delete(newDirector.id);
        return newIds;
      });
    }
    setValue(individualKeyPositionFormState.type, newDirector.id.toString());
  };

  const onEditKeyPosition = (dirId: number, position: TUAEKeyPositions) => {
    const selectedDirector = allPeopleInCompany?.directors?.find(
      (dir) => dir.id === dirId
    );
    if (selectedDirector) {
      setIndividualKeyPositionFormState({
        type: position,
        director: selectedDirector,
      });
      addEditKeyPositionsModalVisibility.show();
    }
  };

  const validateFormData = (formData: TKeyPositionFieldTypes) => {
    const failedDirectorIds = [];
    let validationResult = true;
    Object.values(formData).forEach((dirId) => {
      const directorData = allPeopleInCompany?.directors?.find(
        (eachDirector) => eachDirector.id.toString() === dirId
      );
      if (!directorData) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong. Please try again, or contact Team Commenda if you need help.',
        });
        return { success: false };
      }
      const eachValidationResult = validateIndividualDirector(directorData);
      if (!eachValidationResult.success)
        failedDirectorIds.push(directorData.id);
      validationResult = validationResult && eachValidationResult.success;
    });
    setDirectorIdsWithError(new Set(failedDirectorIds));
    return { success: validationResult };
  };

  const getDirectorPayload = (formData: TKeyPositionFieldTypes) => {
    const payload = [];
    // get all the board position grouped by Ids.
    // {dirId: ['Secretary', 'BankSignatory']}
    const directorIdsVsTitles = groupTitlesByDirectorIds(formData);
    Object.entries(directorIdsVsTitles).forEach(
      ([directorId, directorTitles]) => {
        const person = allPeopleInCompany?.directors?.find(
          (dir) => dir.id === +directorId
        );
        const corporationHasDirector = person?.corporationHasDirector?.find(
          ({ corporationId }) =>
            corporationId === workRequest.corporationId && +directorId
        );

        const additionalDirectorInformation = {
          ...omitLegacyOwnershipFlags(
            corporationHasDirector?.additionalDirectorInformation
          ),
          isDirector: (directorTitles as TUAEKeyPositions[]).includes(
            'Director'
          ),
          boardPosition: (directorTitles as TUAEKeyPositions[])
            .filter((title) => title !== 'Director')
            .map((each) => BoardPositionEnum[each]),
        };

        payload.push({
          id: person.id,
          corporationHasDirector: [
            {
              ...corporationHasDirector,
              corporationId: workRequest?.corporationId,
              additionalDirectorInformation,
            },
          ],
        });
      }
    );

    // Remove board positions from other directors of same corporation
    // Set isDirector to false for all other director.
    const selectedDirectorIds = Object.values(formData);
    allPeopleInCompany.directors.forEach((eachPerson) => {
      if (!selectedDirectorIds.includes(eachPerson.id.toString())) {
        const corporationHasDirector = eachPerson?.corporationHasDirector?.find(
          ({ corporationId, directorId }) =>
            corporationId === workRequest.corporationId &&
            eachPerson.id === directorId
        );
        const additionalDirectorInformation = omitLegacyOwnershipFlags(
          corporationHasDirector?.additionalDirectorInformation
        );
        payload.push({
          id: eachPerson.id,
          corporationHasDirector: [
            {
              ...corporationHasDirector,
              corporationId: workRequest?.corporationId,
              additionalDirectorInformation: {
                ...additionalDirectorInformation,
                boardPosition: [],
                isDirector: false,
              },
            },
          ],
        });
      }
    });

    return payload;
  };

  const onSubmit = async (formData: TKeyPositionFieldTypes) => {
    const validationResult = validateFormData(formData);
    if (!validationResult.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Please fill in the missing information for the key position holders by clicking on "Edit". ',
      });
      return;
    }

    const payload = getDirectorPayload(formData);
    const response = await fetcher(
      `workRequest/${workRequest.id}`,
      {
        method: 'POST',
        body: {
          directors: payload,
        },
      },
      undefined
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      return;
    }

    await mutate();
    next();
  };

  useEffect(() => {
    const bankSignatoryPerson = findDirectorByRoleForCorporation(
      allPeopleInCompany?.directors,
      BoardPositionEnum.BankSignatory,
      workRequest.corporationId
    );

    const managerPerson = findDirectorByRoleForCorporation(
      allPeopleInCompany?.directors,
      BoardPositionEnum.Manager,
      workRequest.corporationId
    );

    const secretaryPerson = findDirectorByRoleForCorporation(
      allPeopleInCompany?.directors,
      BoardPositionEnum.Secretary,
      workRequest.corporationId
    );

    const allDirectorsInCompany = allPeopleInCompany?.directors?.filter((dir) =>
      dir.corporationHasDirector?.find(
        (chd) =>
          chd.corporationId === workRequest.corporationId &&
          chd.directorId === dir.id &&
          chd.additionalDirectorInformation?.isDirector
      )
    );

    const directorPerson =
      allDirectorsInCompany?.length > 0 ? allDirectorsInCompany[0] : null;

    setValue(
      BoardPositionEnum.BankSignatory,
      bankSignatoryPerson?.id?.toString()
    );
    setValue(BoardPositionEnum.Manager, managerPerson?.id?.toString());
    setValue(BoardPositionEnum.Secretary, secretaryPerson?.id?.toString());
    setValue('Director', directorPerson?.id?.toString());
  }, [allPeopleInCompany]);

  if (!allPeopleInCompany || error) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-2">
        <h2 className="txt-large-bold">Key Positions</h2>
        <p className="txt-small-regular">
          Appoint individuals to key positions. Note: One individual can hold
          each position.
        </p>
      </div>

      <FieldWrapper>
        <Label>
          Manager <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="manager-input"
          options={directorOptions || []}
          value={watch(BoardPositionEnum.Manager)}
          setValue={(val) => setValue(BoardPositionEnum.Manager, val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new manager',
            onAddItem: () => {
              setIndividualKeyPositionFormState({
                type: BoardPositionEnum.Manager,
                director: null,
              });
              addEditKeyPositionsModalVisibility.show();
            },
          }}
          error={
            !watch(BoardPositionEnum.Manager) &&
            errors?.[BoardPositionEnum.Manager]?.message
              ? errors?.[BoardPositionEnum.Manager]?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(
            parseInt(watch(BoardPositionEnum.Manager), 10)
          ) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditKeyPosition(
                    parseInt(watch(BoardPositionEnum.Manager), 10),
                    BoardPositionEnum.Manager
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Secretary <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="secretary-input"
          options={directorOptions || []}
          value={watch(BoardPositionEnum.Secretary)}
          setValue={(val) => setValue(BoardPositionEnum.Secretary, val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new secretary',
            onAddItem: () => {
              setIndividualKeyPositionFormState({
                type: BoardPositionEnum.Secretary,
                director: null,
              });
              addEditKeyPositionsModalVisibility.show();
            },
          }}
          error={
            !watch(BoardPositionEnum.Secretary) &&
            errors?.[BoardPositionEnum.Secretary]?.message
              ? errors?.[BoardPositionEnum.Secretary]?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(
            parseInt(watch(BoardPositionEnum.Secretary), 10)
          ) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditKeyPosition(
                    parseInt(watch(BoardPositionEnum.Secretary), 10),
                    BoardPositionEnum.Secretary
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Director <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="director-input"
          options={directorOptions || []}
          value={watch('Director')}
          setValue={(val) => setValue('Director', val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new director',
            onAddItem: () => {
              setIndividualKeyPositionFormState({
                type: 'Director',
                director: null,
              });
              addEditKeyPositionsModalVisibility.show();
            },
          }}
          error={
            !watch('Director') && errors?.Director?.message
              ? errors?.Director?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(parseInt(watch('Director'), 10)) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditKeyPosition(parseInt(watch('Director'), 10), 'Director')
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Bank Signatory <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="bankSignatory-input"
          options={directorOptions || []}
          value={watch(BoardPositionEnum.BankSignatory)}
          setValue={(val) => setValue(BoardPositionEnum.BankSignatory, val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new bank signatory',
            onAddItem: () => {
              setIndividualKeyPositionFormState({
                type: BoardPositionEnum.BankSignatory,
                director: null,
              });
              addEditKeyPositionsModalVisibility.show();
            },
          }}
          error={
            !watch(BoardPositionEnum.BankSignatory) &&
            errors?.[BoardPositionEnum.BankSignatory]?.message
              ? errors?.[BoardPositionEnum.BankSignatory]?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(
            parseInt(watch(BoardPositionEnum.BankSignatory), 10)
          ) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditKeyPosition(
                    parseInt(watch(BoardPositionEnum.BankSignatory), 10),
                    BoardPositionEnum.BankSignatory
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <div className="flex gap-4 items-center">
        <Button variant="secondary" onClick={back}>
          <Icons.ArrowLeft className="size-5" />
          <span>Back</span>
        </Button>
        <Button size="lg" onClick={handleSubmit(onSubmit)}>
          <span>Proceed</span>
          <Icons.ArrowRight className="size-5" />
        </Button>
      </div>

      {addEditKeyPositionsModalVisibility.visible ? (
        <IndividualDirectorFormModal
          isOpen={addEditKeyPositionsModalVisibility.visible}
          onClose={addEditKeyPositionsModalVisibility.hide}
          workRequest={workRequest}
          setNotification={setNotification}
          director={individualKeyPositionFormState.director}
          onSuccess={onSuccessAddEditKeyPosition}
        />
      ) : null}
    </div>
  );
};
